import React, { useEffect, useRef, useState } from "react";
import {
	EExtraDataMappingType,
	IDocumentForUI,
	ILineItemForUI,
	IMappingExtraData,
	ITupleOutputForUI,
} from "../../../shared/common/EstimateEngine/EstimateEngineTypes";
import RestAPI from "../../../utils/restAPI";
import { logDebug, logError } from "../../../shared/common/logger";
import { getMappingExtraDataContent } from "./EstimateTable/EstimateTableRow";

import { getProcessedEstimatesStore, getProcessedEstimatesStoreDebug } from "../../../data/ProcessedEstimatesStore";
import { observer } from "mobx-react";
import { selectablesMap } from "../../../shared/common/EstimateEngine/TicSelectables";
import Select, { components, MultiValue, MultiValueProps, StylesConfig } from "react-select";
import { LassoSimpleButtonFull } from "../../../components/ui/utils/LassoButtons";
import { ImageCanvas } from "../../../components/ui/utils/ImageCropping";
import { getEstimaticsCurrentProjectStore } from "../../../data/DataStores";
import { toastNotify } from "../../../shared/client/FrontendUtils";
import { DocumentWord, IWordWithConfidence } from "../../../shared/common/Azure";
import EngineUtils from "../../../shared/common/EngineUtils";
import EngineSharedUtils, { IQuantityFormula } from "../../../shared/common/EstimateEngine/EngineSharedUtils";
import { getMeasurementByTupleId } from "./EstimateTable/LineEditingDialog";
import TopMostPortal from "../../../components/ui/utils/Portals";

// Define the shape of your option
export interface SelectOption {
	value: string;
	label: string;
}

// Custom styles for react-select
const customStyles: StylesConfig<SelectOption, true> = {
	control: (provided) => ({
		...provided,
		marginTop: "2px",
		minHeight: "42px",
		height: "42px",
	}),
	multiValue: (provided) => ({
		...provided,
		fontSize: "12px", // Smaller font size
		padding: "2px",
	}),
	multiValueLabel: (provided) => ({
		...provided,
		padding: "0 6px",
		cursor: "pointer", // Indicate that it's clickable
	}),
	dropdownIndicator: (provided) => ({
		...provided,
		padding: "4px",
	}),
};

// Custom MultiValue component without the "x" and with clickable label
const CustomMultiValue: React.FC<MultiValueProps<SelectOption>> = (props) => {
	const { data, removeProps } = props;

	const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
		event.stopPropagation(); // Prevent triggering the select dropdown
		removeProps.onClick!(event);
	};

	return (
		<components.MultiValue {...props}>
			<div
				onClick={handleClick}
				style={{
					display: "flex",
					alignItems: "center",
				}}
				role="button"
				aria-label={`Remove ${data.label}`}
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						handleClick(e as unknown as React.MouseEvent<HTMLDivElement>);
					}
				}}
			>
				<span>{data.label}</span>
			</div>
		</components.MultiValue>
	);
};

export function errorTextFromTuple(mappingExtraData: IMappingExtraData[], lowConfidenceArray?: DocumentWord[]): string[] {
	let errors: string[] = [];
	mappingExtraData?.forEach((med) => {
		if (med.type === EExtraDataMappingType.NoEmptyValueNoDefault) {
			errors.push("Some required values were not supplied.");
		} else if (med.type === EExtraDataMappingType.InputWithNoMapping) {
			errors.push("Some writing on the tic sheet was not recognized.");
		} else if (med.type === EExtraDataMappingType.NotAllMappingsUsed) {
			errors.push("Some tic sheet writing was not used in the line item.");
		} else if (med.type === EExtraDataMappingType.QuantityOutOfRange) {
			errors.push("The quantity given in the tic sheet falls outside of the expected range.");
		} else if (med.type === EExtraDataMappingType.QuantityOutOfRangeTPARule) {
			errors.push("The quantity given in the tic sheet falls outside of the TPA's allowed range.");
		} else if (med.type === EExtraDataMappingType.LowConfidenceInput) {
			errors.push("We have low confidence in our interpretation.");
		}
	});
	if (lowConfidenceArray && lowConfidenceArray.length > 0) {
		errors.push("We have low confidence in our interpretation.");
	}
	errors = errors.filter((v, i, a) => a.indexOf(v) === i);
	return errors;
}

export function getLikelyComplementTuples(tuple: ITupleOutputForUI, pageId: string): ITupleOutputForUI[] {
	const parentId = tuple.tupleId;
	const pageInfo = getProcessedEstimatesStore().allCurrentViewPages.find((pi) => {
		const pageInfoData = EngineUtils.pageDataFromPageInfo(pi);
		return pageInfoData.pageId === pageId;
	});
	if (!pageInfo) return [];
	const allTuples = [...pageInfo.page.tuplesOutput].filter((tuple) => !tuple.deleted);

	return allTuples.filter((t) => t.isComplementOf === parentId);
}

export const ErrorResolutionPanel: React.FC<{ tuple: ITupleOutputForUI; pageId: string }> = observer(({ tuple, pageId }) => {
	const processedEstimatesStore = getProcessedEstimatesStore();
	const processedEstimatesStoreDebug = getProcessedEstimatesStoreDebug();
	const currentEstimateStore = getEstimaticsCurrentProjectStore();
	const selectables = (tuple.originalInput?.selections || []).map((s) => s.toLowerCase());
	const leftCellOriginal = (tuple.originalInput?.left.map((w) => w.word) || []).map((w) => w.toLowerCase());
	const leftCellInput = leftCellOriginal.filter((w) => !selectables.includes(w)).join(" ");
	const selected = selectables.filter((s) => leftCellOriginal.includes(s));
	const [fixingInput, setFixingInput] = useState(true);
	const [leftCell, setLeftCell] = useState(leftCellInput);
	const [rightCell, setRightCell] = useState(tuple.originalInput?.right.map((w) => w.word).join(" ") || "");
	const [selection, setSelection] = useState<MultiValue<SelectOption>>(selected.map((s) => ({ value: s, label: s })));
	const [displayLines, setDisplayLines] = useState<
		{ line: string; initialFormule?: IQuantityFormula; formula?: IQuantityFormula; qnt: string; initialQnt?: string }[]
	>([]);
	const [canShowInput, setCanShowInput] = useState(false);
	const [mappingExtraData, setMappingExtraData] = useState(tuple.lineItems[0].mappingExtraData);
	const [lowConfidenceArray, setLowConfidenceArray] = useState<DocumentWord[]>(tuple.lowConfidenceArray ?? []);
	const lastCallProps = useRef({ tid: "", left: "", right: "", sel: "" });
	const newCallProps = useRef({ tid: "", left: "", right: "", sel: "" });
	const [iDocumentForUI, setIDocumentForUI] = useState<IDocumentForUI>();
	const autoGenInterval = useRef<any>(null);
	const [useAutoGen, setUseAutoGen] = useState(true);
	const useAutoGenRef = useRef(useAutoGen);
	const [errorTexts, setErrorTexts] = useState<string[]>([]);
	const [lineCheckedArray, setLineCheckedArray] = useState<boolean[]>([true, true, true, true, true, true]);
	const [complementLineCheckedArray, setComplementLineCheckedArray] = useState<boolean[]>([true, true, true, true, true, true]);
	const [showingComplementUI, setShowingComplementUI] = useState(false);
	const [haveComplements, setHaveComplements] = useState(false);
	const [parentLineItem, setParentLineItem] = useState<ILineItemForUI | null>(null);
	const [showImagePreview, setShowImagePreview] = useState<ITupleOutputForUI>();
	function setLineChecked(_showingComplementUI: boolean, index: number, value: boolean) {
		if (_showingComplementUI) {
			const newArray = [...complementLineCheckedArray];
			newArray[index] = value;
			setComplementLineCheckedArray(newArray);
		} else {
			const newArray = [...lineCheckedArray];
			newArray[index] = value;
			setLineCheckedArray(newArray);
		}
	}
	function getLineChecked(_showingComplementUI: boolean, index: number) {
		if (_showingComplementUI) {
			return complementLineCheckedArray[index];
		} else {
			return lineCheckedArray[index];
		}
	}

	useEffect(() => {
		useAutoGenRef.current = useAutoGen;
	}, [useAutoGen]);

	useEffect(() => {
		setErrorTexts(errorTextFromTuple(mappingExtraData, lowConfidenceArray));
	}, [mappingExtraData, lowConfidenceArray]);

	useEffect(() => {
		if (showingComplementUI) {
			return;
		}
		(async () => {
			await refreshLineItemData(true);
		})();
		setDisplayLinesFromTuple(tuple);
		const likelyComplementTuples = getLikelyComplementTuples(tuple, pageId);
		const lineItems = [...tuple.lineItems, ...likelyComplementTuples.flatMap((t) => t.lineItems)];
		setHaveComplements(lineItems.some((li) => !li.deleted && li.isComplement));

		if (!autoGenInterval.current) {
			autoGenInterval.current = setInterval(async () => {
				if (useAutoGenRef.current) {
					// logDebug('autoGenInterval');
					refreshLineItemData(false);
				}
			}, 500);
		}
		return () => {
			if (autoGenInterval.current) {
				clearInterval(autoGenInterval.current);
				autoGenInterval.current = null;
			}
		};
	}, [showingComplementUI]);

	useEffect(() => {
		if (!showingComplementUI) {
			return;
		}
		setDisplayLinesFromTuple(tuple);
	}, [showingComplementUI]);

	useEffect(() => {
		const tid = tuple.tupleId;
		const left = encodeURIComponent(leftCell.toLowerCase());
		const right = encodeURIComponent(rightCell.toLowerCase());
		const sel = selection
			.map((s) => s.label.replaceAll("(", "").replaceAll(")", ""))
			.join(",")
			.toLowerCase();
		newCallProps.current = { tid, left, right, sel };
	}, [leftCell, rightCell, selection]);

	const handleChange = (selected: any, actionMeta: any) => {
		setSelection(selected);
	};

	function setDisplayLinesFromTuple(tuple: ITupleOutputForUI) {
		let _displayLines: { line: string; qnt: string }[] = [];

		// retrieve likely complements that are saved in their own individual tuples
		// and add them to the "line items to show"
		const likelyComplementTuples = getLikelyComplementTuples(tuple, pageId);
		const lineItems = [...tuple.lineItems, ...likelyComplementTuples.flatMap((t) => t.lineItems)];

		for (const line of lineItems) {
			if (line.deleted) continue;
			if (showingComplementUI) {
				if (line.isComplement) {
					_displayLines.push({ line: line.lineItem, qnt: EngineSharedUtils.getQuantityStringForLineItem(line) });
				} else {
					setParentLineItem(line);
				}
			} else if (!line.isComplement) {
				_displayLines.push({ line: line.lineItem, qnt: EngineSharedUtils.getQuantityStringForLineItem(line) });
			}
		}
		setDisplayLines(_displayLines);
		setCanShowInput(true);
	}

	async function refreshLineItemData(force: boolean): Promise<void> {
		// logDebug('generateNewLineItem0');
		if (
			!force &&
			lastCallProps.current.tid === newCallProps.current.tid &&
			lastCallProps.current.left === newCallProps.current.left &&
			lastCallProps.current.right === newCallProps.current.right &&
			lastCallProps.current.sel === newCallProps.current.sel
		) {
			return;
		}
		// logDebug('generateNewLineItem1', newCallProps.current, lastCallProps.current);
		if (!newCallProps.current.tid) {
			return;
		}
		try {
			const url = RestAPI.getServerBaseUrl(
				`/rest/estimates/generateLineItemFromInput?tupleId=${newCallProps.current.tid}&leftContent=${newCallProps.current.left}&rightContent=${newCallProps.current.right}&selectable=${newCallProps.current.sel}`,
			);
			const forUI = (await RestAPI.fetchURL(url, "GET")) as IDocumentForUI;
			// logDebug('forUI', forUI.pages[0].tuplesOutput[0]);
			setIDocumentForUI(forUI);
			setDisplayLinesFromTuple(forUI.page.tuplesOutput[0]);
			setMappingExtraData(forUI.page.tuplesOutput[0].lineItems[0].mappingExtraData);
			// setLowConfidenceArray(forUI.page.tuplesOutput[0].lowConfidenceArray ?? []);
			lastCallProps.current = { ...newCallProps.current };
		} catch (err) {
			logError("generateNewLineItem", err);
		}
	}

	const showingRadioOptions = displayLines.length > 1 || showingComplementUI;
	return (
		<div className="w-[1026px] min-h-[600px] bg-white rounded-xl shadow flex-col justify-start items-start inline-flex relative">
			<div className="self-stretch h-[73px] border-b border-[#344053] flex-col justify-start items-center flex relative">
				<div className="self-stretch px-6 pt-6 bg-white justify-start items-start gap-4 inline-flex  mx-4">
					{processedEstimatesStoreDebug.showQAPanel && (
						<div className="absolute top-1 left-1 border">
							<input type="checkbox" checked={useAutoGen} onChange={(e) => setUseAutoGen(e.target.checked)} />
							auto gen
						</div>
					)}
					<div className="grow shrink basis-0 flex-col justify-start items-start gap-1 inline-flex">
						<div className="self-stretch text-[#0f1728] text-xl font-semibold font-['Hanken Grotesk'] leading-7">
							Resolve issue with{" "}
							<span className="text-blue-700">
								{showingComplementUI
									? parentLineItem?.lineItem || "unknown line item"
									: displayLines?.[0]?.line || "unknown line item"}
							</span>
						</div>
					</div>
				</div>
				<div
					className="absolute right-4 top-4 btn"
					onClick={() => (processedEstimatesStore.showErrorResolutionPanelForTuple = undefined)}
				>
					X
				</div>
			</div>
			<div className="self-stretch  min-h-[452px] px-8 py-3 flex-col justify-start items-center gap-2 flex ">
				{fixingInput && (
					<div className="relative">
						<div className="justify-start items-center gap-8 inline-flex">
							<div className="text-[#0f1728] h-[76px]text-base font-medium font-['Hanken Grotesk'] leading-normal no-break relative mb-4">
								Please provide the correct inputs:
							</div>
							<div className="grow shrink basis-0 h-[86px] justify-start items-end gap-4 flex">
								<div className="grow shrink basis-0 h-[86px] justify-start items-center gap-4 flex">
									<div className="grow shrink basis-0 flex-col justify-start items-start gap-1.5 inline-flex">
										<div className="self-stretch h-[60px] flex-col justify-start items-start gap-1.5 flex">
											<div className="text-[#344053] text-xs font-medium font-['Hanken Grotesk'] leading-[14.40px]">
												Cell input
											</div>
											<div className="self-stretch px-3 py-2 bg-white rounded-lg shadow border border-[#cfd4dc] justify-start items-center gap-2 inline-flex">
												<div className="grow shrink basis-0 h-6 justify-start items-center gap-2 flex">
													<input
														type="text"
														className="grow focus:outline-none w-32 border-0 shrink basis-0 text-[#0f1728] text-base font-normal font-['Hanken Grotesk'] leading-normal"
														value={leftCell}
														onChange={(e) => setLeftCell(e.target.value)}
													/>
												</div>
											</div>
										</div>
										<div className="self-stretch opacity-0 text-[#475466] text-xs font-normal font-['Hanken Grotesk'] leading-tight">
											This is a hint text to help user.
										</div>
									</div>
									<div className="grow shrink basis-0 flex-col justify-start items-start gap-1.5 inline-flex">
										<div className="self-stretch h-[60px] flex-col justify-start items-start gap-1.5 flex">
											<div className="text-[#344053] text-xs font-medium font-['Hanken Grotesk'] leading-[14.40px]">
												Documented Quantity
											</div>
											<div className="self-stretch px-3 py-2 bg-white rounded-lg shadow border border-[#cfd4dc] justify-start items-center gap-2 inline-flex">
												<div className="grow shrink basis-0 h-6 justify-start items-center gap-2 flex">
													<input
														type="text"
														className="grow focus:outline-none w-32 border-0 shrink basis-0 text-[#0f1728] text-base font-normal font-['Hanken Grotesk'] leading-normal"
														value={rightCell}
														onChange={(e) => setRightCell(e.target.value)}
													/>
												</div>
											</div>
										</div>
										<div className="self-stretch opacity-0 text-[#475466] text-xs font-normal font-['Hanken Grotesk'] leading-tight">
											This is a hint text to help user.
										</div>
									</div>
									<div className="grow shrink basis-0 flex-col justify-start items-start gap-5 inline-flex">
										<div className="self-stretch h-[60px] flex-col justify-start items-start gap-1.5 flex">
											<div className="text-[#344053] text-xs font-medium font-['Hanken Grotesk'] leading-[14.40px]">
												Selection
											</div>
											<div className="grow shrink basis-0 h-6 justify-start items-center gap-2 flex">
												<Select
													// placeholder='Select'
													className="w-64 grow shrink focus:outline-none basis-0 text-[#667084] text-base font-normal font-['Hanken Grotesk'] leading-normal"
													options={[...(selectablesMap[tuple.tupleId] ?? [])].map((s) => ({
														value: s,
														label: s,
													}))}
													isMulti
													styles={customStyles}
													isClearable={false}
													components={{
														MultiValue: CustomMultiValue,
														MultiValueRemove: () => null,
													}}
													value={selection}
													onChange={handleChange}
												/>
											</div>
										</div>
										<div className="self-stretch opacity-0 text-[#475466] text-xs font-normal font-['Hanken Grotesk'] leading-tight">
											This is a hint text to help user.
										</div>
									</div>
								</div>
								<div
									className="w-[92px] self-stretch pb-5 flex-col justify-end items-start gap-2 inline-flex btn"
									onClick={() => refreshLineItemData(true)}
								>
									<div className="self-stretch px-4 py-2.5 bg-[#eff8ff] rounded-lg border border-[#eff8ff] justify-center items-center gap-2 inline-flex mb-2.5">
										<div className="justify-start items-center gap-2 flex ">
											<div className="text-[#156fee] text-sm font-semibold font-['Hanken Grotesk'] leading-tight">
												Generate
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className="self-stretch text-[#475466] text-xs font-normal font-['Hanken Grotesk'] leading-tight relative -top-4 ">
							<ul
								style={{
									listStyleType: "disc",
								}}
							>
								{errorTexts.map((et) => (
									<li className="ml-4">{et}</li>
								))}
								<br />
							</ul>
						</div>
					</div>
				)}
				{!fixingInput && !showingComplementUI && (
					<div className="h-[110px] self-stretch justify-start items-start gap-10 inline-flex">
						<div className="flex-col justify-center items-center gap-4 inline-flex">
							<div className="w-[667px] text-[#0f1728] text-base font-medium font-['Hanken Grotesk'] leading-normal">
								{errorTexts.length <= 1 ? (
									<>
										{errorTexts.length ? errorTexts[0] : ""} Please accept the line item or make any necessary
										adjustments.
									</>
								) : (
									<>
										We have found the following issues, please accept the line item or edit to fix it:
										<br />
										<ul
											style={{
												listStyleType: "disc",
											}}
										>
											{errorTexts.map((et) => (
												<li className="ml-8">{et}</li>
											))}
											<br />
										</ul>
									</>
								)}
							</div>
						</div>
						<div className="grow shrink basis-0 h-10 justify-end items-center gap-4 flex">
							<div
								className="px-4 py-2.5 bg-[#eff8ff] rounded-lg border border-[#eff8ff] justify-center items-center gap-2 flex btn"
								onClick={() => setFixingInput(true)}
							>
								<div className="justify-start items-center gap-2 flex">
									<img src="/assets/ui/edit-04.svg" className="w-5 h-5 relative" />
									<div className="text-[#156fee] text-sm font-semibold font-['Hanken Grotesk'] leading-tight">
										Fix input
									</div>
								</div>
							</div>
						</div>
					</div>
				)}
				<div className="self-stretch  min-h-[268px] flex-col justify-start items-start gap-4 flex">
					<div className="w-[667px] text-[#0f1728] text-base font-bold font-['Hanken Grotesk'] leading-snug">
						{showingComplementUI ? "Recommended line items to include:" : "The automated results we were able to generate:"}
					</div>
					<div className="w-[962px] max-h-[350px] overflow-y-auto rounded-2xl border border-[#eaecf0] justify-start items-start inline-flex ">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500  tracking-wider w-[50%] min-w-[50%] border-r">
										Tic sheet scan
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500  tracking-wider border-r w-[35%]">
										Line Item
									</th>
									<th
										className={`px-6 py-3 text-left text-xs font-medium text-gray-500  tracking-wider ${
											showingRadioOptions && "border-r"
										}`}
									>
										Documented Quantity
									</th>
									{showingRadioOptions && (
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500  tracking-wider">Action</th>
									)}
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200 ">
								{displayLines.map((line, index) => {
									return (
										<tr key={index}>
											{index === 0 && (
												<td
													onClick={() => setShowImagePreview(tuple)}
													rowSpan={Math.max(3, displayLines.length)}
													className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r relative"
												>
													<ImageCanvas
														className="absolute top-4 left-0 h-8 cursor-pointer"
														url={RestAPI.getStorageFileUrl(tuple.fullPageImagePath!)}
														rect={tuple.tupleRegion}
														paddingPixels={20}
													/>
													{processedEstimatesStoreDebug.showQAPanel &&
														getMappingExtraDataContent(mappingExtraData, true)}
												</td>
											)}
											<td className="px-6 py-4  text-sm text-gray-500 border-r">{displayLines[index].line}&nbsp;</td>
											<td
												className={`px-6 py-4 whitespace-nowrap text-sm text-gray-500 ${
													showingRadioOptions && "border-r"
												}`}
											>
												{canShowInput && showingComplementUI ? (
													<input
														key={displayLines[index].initialQnt}
														className="border px-1"
														defaultValue={displayLines[index].qnt}
														onChange={(e) => {
															const newArray = [...displayLines];
															newArray[index].qnt = e.target.value;
															setDisplayLines(newArray);
														}}
														onBlur={(e) => {
															try {
																const formula = EngineSharedUtils.extractFormulaFromString(e.target.value);
																if (!formula) {
																	throw new Error("Invalid formula");
																} else {
																	const errorText = EngineSharedUtils.checkAndFixFormula(
																		formula,
																		getMeasurementByTupleId(tuple.tupleId),
																	);
																	if (errorText) {
																		throw new Error(errorText);
																	} else {
																		const newArray = [...displayLines];
																		newArray[index].formula = displayLines[index].initialFormule =
																			formula;
																		newArray[index].qnt = displayLines[index].initialQnt =
																			EngineSharedUtils.getFormulaString(formula);
																		setDisplayLines(newArray);
																	}
																}
															} catch (err: any) {
																logError("Quantity not saved", err);
																toastNotify("Quantity not saved: " + err.message, true);
																// restore the initial value
																const newArray = [...displayLines];
																newArray[index].formula = newArray[index].initialFormule;
																newArray[index].qnt = displayLines[index].initialQnt!;
																displayLines[index].initialQnt = "";
																setDisplayLines(newArray);
															}
														}}
														onFocus={(e) => {
															if (!displayLines[index].initialQnt) {
																const newArray = [...displayLines];
																newArray[index].initialQnt = e.target.value;
																setDisplayLines(newArray);
															}
														}}
													/>
												) : (
													<>{displayLines[index].qnt}&nbsp;</>
												)}
											</td>
											{showingRadioOptions && (
												<td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-500  ">
													<input
														type="radio"
														checked={!getLineChecked(showingComplementUI, index)}
														onChange={() => setLineChecked(showingComplementUI, index, false)}
													/>
													<button className="btn m-1 text-red-600  ">Reject</button>
													<input
														type="radio"
														checked={getLineChecked(showingComplementUI, index)}
														onChange={() => setLineChecked(showingComplementUI, index, true)}
													/>
													<button className="btn m-1 text-green-700  ">Approve</button>
												</td>
											)}
										</tr>
									);
								})}
								{/* <tr>
									<td rowSpan={3} className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r">
										<ImageCanvas
											url={RestAPI.getStorageFileUrl(tuple.fullPageImagePath!)}
											rect={tuple.tupleRegion}
											paddingPixels={20}
										/>
										{processedEstimatesStoreDebug.showQAPanel && getMappingExtraDataContent(mappingExtraData, true)}
									</td>
									<td className="px-6 py-4  text-sm text-gray-500 border-r">
										{displayLines.length > 0 && displayLines[0].line}&nbsp;
									</td>
									<td
										className={`px-6 py-4 whitespace-nowrap text-sm text-gray-500 ${showingRadioOptions && "border-r"}`}
									>
										{displayLines.length > 0 && displayLines[0].qnt}&nbsp;
									</td>
									{showingRadioOptions && (
										<td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-500  ">
											<input type="radio" checked={!line0Checked} onChange={() => setLine0Checked(false)} />
											<button className="btn m-1 text-red-600  ">Reject</button>
											<input type="radio" checked={line0Checked} onChange={() => setLine0Checked(true)} />
											<button className="btn m-1 text-green-700  ">Approve</button>
										</td>
									)}
								</tr>
								<tr>
									<td className="px-6 py-4  text-sm text-gray-500 border-r">
										{displayLines.length > 1 && displayLines[1].line}&nbsp;
									</td>
									<td
										className={`px-6 py-4 whitespace-nowrap text-sm text-gray-500 ${
											displayLines.length > 1 && "border-r"
										}`}
									>
										{displayLines.length > 1 && displayLines[1].qnt}&nbsp;
									</td>
									{displayLines.length > 1 && (
										<td className="px-2 py-4 whitespace-nowrap text-sm font-medium ">
											<input type="radio" checked={!line1Checked} onChange={() => setLine1Checked(false)} />
											<button className="btn m-1 text-red-600  ">Reject</button>
											<input type="radio" checked={line1Checked} onChange={() => setLine1Checked(true)} />
											<button className="btn m-1 text-green-700  ">Approve</button>
										</td>
									)}
								</tr> */}
								{/* <tr>
								<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-r">
									{displayLines.length > 2 && displayLines[2].line}&nbsp;
								</td>
								<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
									{displayLines.length > 2 && displayLines[2].qnt}&nbsp;
								</td>
							</tr> */}
							</tbody>
						</table>
					</div>
				</div>
				{
					// fixingInput &&
					<div className="h-[86px] px-6 pt-4 pb-6 w-full justify-between items-center inline-flex absolute bottom-2">
						<div className="justify-end items-center gap-6 flex">
							<div
								className="px-4 py-3 bg-white rounded-lg border border-[#eaecf0] justify-center items-center gap-2 flex btn"
								onClick={() => {
									if (showingComplementUI) {
										setShowingComplementUI(false);
										setFixingInput(true);
										return;
									}
									processedEstimatesStore.showErrorResolutionPanelForTuple = undefined;
								}}
							>
								<div className="justify-start items-center gap-2 flex">
									<div className="text-[#344053] text-base font-semibold font-['Hanken Grotesk'] leading-snug">
										{showingComplementUI ? "Back" : "Cancel"}
									</div>
								</div>
							</div>
						</div>
						<div className="justify-end items-center gap-6 flex">
							{!showingComplementUI && (
								<LassoSimpleButtonFull
									caption={displayLines.length > 1 ? "Discard all line items" : "Discard line item"}
									image="/assets/ui/trash-02.svg"
									customButtonStyle={{
										backgroundColor: "#fef3f2",
									}}
									customTextStyle={{
										color: "#f44336",
									}}
									onClick={async () => {
										if (iDocumentForUI) {
											// this is the line item returned from the server
											currentEstimateStore.showLoadingAnimation = true;
											// when the user delete the line item, we need to update the iDocumentForUI and remove all the errors currently reported there
											iDocumentForUI.page.tuplesOutput[0].deleted = true;
											try {
												await processedEstimatesStore.updateOrCreateLineItem(
													tuple.tupleId,
													currentEstimateStore.estimaticsData.estimateId,
													pageId,
													iDocumentForUI.page.tuplesOutput[0],
												);
											} catch (err) {
												logError("discard line item", err);
											}
											logDebug("discard line item", iDocumentForUI.page.tuplesOutput[0]);
											toastNotify("Line item successfully discarded", false);

											currentEstimateStore.showLoadingAnimation = false;
										}
										processedEstimatesStore.showErrorResolutionPanelForTuple = undefined;
									}}
								/>
							)}
							<LassoSimpleButtonFull
								caption={
									haveComplements
										? showingComplementUI
											? "Apply All"
											: "Approve and Continue"
										: displayLines.length > 1
										? "Apply Actions"
										: "Approve line item"
								}
								onClick={async () => {
									if (haveComplements && !showingComplementUI) {
										setCanShowInput(false);
										setShowingComplementUI(true);
										setFixingInput(false);
										return;
									}
									if (iDocumentForUI) {
										// this is the line item returned from the server
										currentEstimateStore.showLoadingAnimation = true;
										try {
											const originalLineItems = iDocumentForUI.page.tuplesOutput[0].lineItems;

											const complementLineItems = tuple.lineItems.filter((li) => !li.deleted && li.isComplement);
											const likelyTuples = getLikelyComplementTuples(tuple, pageId);
											const likelyComplementLineItems = likelyTuples
												.flatMap((t) => t.lineItems)
												.filter((li) => !li.deleted);

											// Delete likely complement tuples because the line items will be saved under parent now.
											likelyTuples.forEach((t) => {
												t.lineItems[0].deleted = true;
												t.deleted = true;
											});

											if (showingComplementUI) {
												// update the quantity of the complement line items if changed by the user
												// logDebug("what we have", complementLineItems, displayLines);
												for (let i = 0; i < displayLines.length; i++) {
													try {
														if (i < complementLineItems.length) {
															if (displayLines[i].formula) {
																complementLineItems[i].formula = displayLines[i].formula;
																complementLineItems[i].qnm = [];
															} else {
																const qnt = EngineUtils.verifyQuantity(displayLines[i].qnt);
																complementLineItems[i].qnm[0] = {
																	measurement: qnt.text,
																	quantity: qnt.number,
																	modifier: qnt.modifier,
																};
															}
														} else {
															if (displayLines[i].formula) {
																likelyComplementLineItems[i - complementLineItems.length].formula =
																	displayLines[i].formula;
																likelyComplementLineItems[i - complementLineItems.length].qnm = [];
															} else {
																const qnt = EngineUtils.verifyQuantity(displayLines[i].qnt);
																likelyComplementLineItems[i - complementLineItems.length].qnm[0] = {
																	measurement: qnt.text,
																	quantity: qnt.number,
																	modifier: qnt.modifier,
																};
															}
														}
													} catch (err) {
														// nothing to do. this can happen when user didn't change the quantity
													}
												}
											}
											// when the user approves the line item, we need to update the iDocumentForUI and remove all the errors currently reported there
											iDocumentForUI.page.tuplesOutput[0].hasError = false;
											originalLineItems.forEach((li) => {
												li.mappingExtraData = [];
												if (EngineSharedUtils.getQuantityStringForLineItem(li, "N/A") !== "N/A") {
													li.confirmed = true;
												}
											});
											lineCheckedArray.slice(0, originalLineItems.length).forEach((checked, i) => {
												originalLineItems[i].deleted = !checked;
											});
											logDebug(complementLineItems);
											logDebug(likelyComplementLineItems);
											complementLineCheckedArray
												.slice(0, complementLineItems.length + likelyComplementLineItems.length)
												.forEach((checked, i) => {
													if (i < complementLineItems.length) {
														complementLineItems[i].deleted = !checked;
														if (
															EngineSharedUtils.getQuantityStringForLineItem(
																complementLineItems[i],
																"N/A",
															) !== "N/A"
														) {
															complementLineItems[i].confirmed = checked;
														}
													} else {
														likelyComplementLineItems[i - complementLineItems.length].deleted = !checked;
														if (
															EngineSharedUtils.getQuantityStringForLineItem(
																likelyComplementLineItems[i - complementLineItems.length],
																"N/A",
															) !== "N/A"
														) {
															likelyComplementLineItems[i - complementLineItems.length].confirmed = checked;
														}
													}
												});
											iDocumentForUI.page.tuplesOutput[0].lineItems = [
												...originalLineItems,
												...complementLineItems,
												...likelyComplementLineItems,
											];
											iDocumentForUI.page.tuplesOutput[0].lineItems.forEach((li) => {
												if (EngineSharedUtils.getQuantityStringForLineItem(li, "N/A") !== "N/A") {
													li.confidence = 1;
												}
											});
											iDocumentForUI.page.tuplesOutput[0].deleted =
												iDocumentForUI.page.tuplesOutput[0].lineItems.every((li) => li.deleted);
											await processedEstimatesStore.updateOrCreateLineItem(
												tuple.tupleId,
												currentEstimateStore.estimaticsData.estimateId,
												pageId,
												{
													...iDocumentForUI.page.tuplesOutput[0],
													tupleRegion: tuple.tupleRegion,
													hadErrorThatHasBeenFixed: true,
												},
												"ErrorResolutionPanel",
												{ left: leftCell, right: rightCell, selections: selection },
											);
											logDebug("approveLineItem", iDocumentForUI.page.tuplesOutput[0]);
											toastNotify("Line item successfully saved", false);

											await Promise.all(
												likelyTuples.map(async (t) => {
													await getProcessedEstimatesStore().updateOrCreateLineItem(
														t.tupleId,
														currentEstimateStore.estimaticsData.estimateId,
														pageId,
														t,
													);
												}),
											);
										} catch (err) {
											logError("approveLineItem", err);
											toastNotify("Error saving the line item", true);
										}
										currentEstimateStore.showLoadingAnimation = false;
									}
									processedEstimatesStore.showErrorResolutionPanelForTuple = undefined;
								}}
								disabled={false}
							/>
						</div>
					</div>
				}
			</div>
			{!!showImagePreview && (
				<TopMostPortal>
					<div
						onClick={() => setShowImagePreview(undefined)}
						className="fixed inset-0 flex items-center justify-center cursor-pointer"
					>
						<ImageCanvas
							className="h-32 "
							url={RestAPI.getStorageFileUrl(showImagePreview.fullPageImagePath!)}
							rect={showImagePreview.tupleRegion}
							paddingPixels={60}
						/>
					</div>
				</TopMostPortal>
			)}
		</div>
	);
});
